import { useState } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { Modal } from '../modal';
import { Button } from '../button';
import { useMutation } from '@apollo/client';
import { FORGOT_PASSWORD, RESEND_CONFIRMATION_CODE } from '../../services/authService/queries';
import { toast } from 'react-toastify';
import {
  IResendConfirmationCodeMutation,
  IResendConfirmationCodeMutationVariables,
  ISendForgotPasswordEmailMutation,
  ISendForgotPasswordEmailMutationVariables,
} from '../../services/authService/__generated__/queries.generated';
import { modalActions } from '../../../store/authStore/modalStore';
import { Colors } from '../../styles/colors';
import { AUTH_FORM_COPY, isValidEmailAddress } from '../../utils/authFormValidation';

const Container = styled.div`
  ${tw`w-full max-w-md box-border`}
`;

const Title = styled.h2`
  ${tw`text-xl md:text-2xl font-bold mb-6`}
`;

const FormGroup = styled.div`
  ${tw`mb-4 w-full`}
`;

const Label = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-2`}
`;

const Input = styled.input<{ $invalid?: boolean }>`
  ${tw`w-full border border-border-default border-solid rounded-md h-[44px] px-3 max-w-full min-w-0 box-border`}
  ${({ $invalid }) =>
    $invalid &&
    css`
      border-color: ${Colors.Error};
    `}
`;

const FieldError = styled.p`
  ${tw`text-sm mt-1 mb-0`}
  color: ${Colors.Error};
`;

interface ForgotPwdModalProps {
  isOpen: boolean;
}

const isUnconfirmedAccountError = (message: string) => {
  const lower = message.toLowerCase();
  return (
    lower.includes('not confirmed') ||
    lower.includes('unconfirmed') ||
    lower.includes('not verified')
  );
};

const ForgotPwdModal = ({ isOpen }: ForgotPwdModalProps) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();

  const [resendConfirmationCode, { loading: resendConfirmationCodeLoading }] = useMutation<
    IResendConfirmationCodeMutation,
    IResendConfirmationCodeMutationVariables
  >(RESEND_CONFIRMATION_CODE);

  const sendVerificationInstead = async () => {
    const trimmedEmail = email.trim();
    modalActions.setCurrentEmail(trimmedEmail);

    try {
      await resendConfirmationCode({ variables: { email: trimmedEmail } });
      toast.success('This email is not verified yet. We sent a new verification code.');
    } catch {
      toast.info('This email is not verified yet. Please enter your verification code.');
    }

    modalActions.closeForgotPwdModal();
    modalActions.openVerifyCodeModal(trimmedEmail);
  };

  const [forgotPassword, { loading: forgotPasswordLoading }] = useMutation<
    ISendForgotPasswordEmailMutation,
    ISendForgotPasswordEmailMutationVariables
  >(FORGOT_PASSWORD, {
    onCompleted: () => {
      toast.success('Please check your email for a reset link');

      handleForgotPasswordDone();
    },
    onError: error => {
      if (isUnconfirmedAccountError(error.message || '')) {
        sendVerificationInstead();
        return;
      }

      toast.error(error.message);
    },
  });

  const handleForgotPassword = () => {
    if (!isValidEmailAddress(email)) {
      setEmailError(AUTH_FORM_COPY.invalidEmail);
      toast.error(AUTH_FORM_COPY.invalidEmail);
      return;
    }

    forgotPassword({
      variables: {
        email: email.trim(),
      },
    });
  };

  const handleForgotPasswordDone = () => {
    modalActions.setCurrentEmail(email);
    modalActions.closeForgotPwdModal();
    modalActions.openResetPwdModal();
  };

  const handleSetEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError(undefined);
  };

  return (
    <Modal isOpen={isOpen} onClose={modalActions.closeForgotPwdModal}>
      <Container>
        <Title>Forgot Password</Title>
        <FormGroup>
          <Label>Email</Label>
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={handleSetEmail}
            $invalid={!!emailError}
            aria-invalid={!!emailError}
          />
          {emailError && <FieldError>{emailError}</FieldError>}
        </FormGroup>
        <Button
          text="Send Reset Link"
          onClick={handleForgotPassword}
          isDisabled={forgotPasswordLoading || resendConfirmationCodeLoading}
          isLoading={forgotPasswordLoading || resendConfirmationCodeLoading}
          buttonWidth="100%"
        />
      </Container>
    </Modal>
  );
};

export default ForgotPwdModal;
